import { useCallback, useEffect, useState } from "react";
import { FileText, Download, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";

const TYPES = [
  { key: "commissioning", label: "Commissioning" },
  { key: "blueprint",     label: "Blueprint" },
];
const BYTES_PER_KB = 1024;

export default function DocumentsPage() {
  const { t } = useLanguage();
  const [docs, setDocs] = useState([]);
  const [type, setType] = useState("commissioning");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    project_name: "", site: "", client: "", engineer: "",
    systems_csv: "Access Control, VMS",
    scope: "", budget: 0, timeline: "",
    sites_csv: "",
    notes: "",
  });

  const load = useCallback(() => {
    return api.get("/documents").then(r => setDocs(r.data)).catch(() => setDocs([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (type === "commissioning") {
        await api.post("/documents/commissioning", {
          project_name: form.project_name, site: form.site, client: form.client, engineer: form.engineer,
          systems: form.systems_csv.split(",").map(s => s.trim()).filter(Boolean),
          hardware: [], notes: form.notes,
        });
      } else {
        await api.post("/documents/blueprint", {
          project_name: form.project_name, client: form.client, scope: form.scope,
          systems: form.systems_csv.split(",").map(s => s.trim()).filter(Boolean),
          sites: form.sites_csv.split(",").map(s => s.trim()).filter(Boolean),
          budget: Number(form.budget), timeline: form.timeline, notes: form.notes,
        });
      }
      toast.success("Document generated.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const downloadUrl = (id) => `${API}/documents/download/${id}`;

  const download = async (doc) => {
    try {
      const res = await fetch(downloadUrl(doc.document_id), { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${doc.name || "document"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(String(err.message || err)); }
  };

  return (
    <div className="p-6 lg:p-10 space-y-8 max-w-6xl">
      <header>
        <div className="text-overline">06 · Document Generator</div>
        <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight text-white mt-2" data-testid="docs-title">{t("docs.title")}</h1>
        <p className="mt-2 text-sm text-secondary-foreground/70">{t("docs.subtitle")}</p>
      </header>

      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="p-6 border border-border rounded-sm bg-card space-y-4" data-testid="docs-form">
          <F label={t("docs.type")}>
            <div className="flex gap-2" data-testid="docs-type-picker">
              {TYPES.map(tp => (
                <button type="button" key={tp.key} onClick={() => setType(tp.key)}
                  className={`flex-1 h-10 rounded-sm border text-[10px] font-mono tracking-[0.22em] uppercase transition-colors ${type === tp.key ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/50"}`}
                  data-testid={`docs-type-${tp.key}`}>{tp.label}</button>
              ))}
            </div>
          </F>
          <F label="Project name"><input required value={form.project_name} onChange={e => setForm({ ...form, project_name: e.target.value })} className={inp} data-testid="docs-project-input" /></F>
          <F label="Client"><input required value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} className={inp} data-testid="docs-client-input" /></F>
          {type === "commissioning" ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <F label="Site"><input required value={form.site} onChange={e => setForm({ ...form, site: e.target.value })} className={inp} data-testid="docs-site-input" /></F>
                <F label="Lead engineer"><input required value={form.engineer} onChange={e => setForm({ ...form, engineer: e.target.value })} className={inp} data-testid="docs-engineer-input" /></F>
              </div>
              <F label="Systems (comma)"><input value={form.systems_csv} onChange={e => setForm({ ...form, systems_csv: e.target.value })} className={inp} data-testid="docs-systems-input" /></F>
            </>
          ) : (
            <>
              <F label="Scope"><textarea rows={3} value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} className={`${inp} h-auto py-2`} data-testid="docs-scope-input" /></F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Budget USD"><input type="number" min={0} value={form.budget} onChange={e => setForm({ ...form, budget: e.target.value })} className={inp} data-testid="docs-budget-input" /></F>
                <F label="Timeline"><input value={form.timeline} onChange={e => setForm({ ...form, timeline: e.target.value })} className={inp} data-testid="docs-timeline-input" /></F>
              </div>
              <F label="Systems (comma)"><input value={form.systems_csv} onChange={e => setForm({ ...form, systems_csv: e.target.value })} className={inp} data-testid="docs-systems-input" /></F>
              <F label="Sites (comma)"><input value={form.sites_csv} onChange={e => setForm({ ...form, sites_csv: e.target.value })} className={inp} data-testid="docs-sites-input" /></F>
            </>
          )}
          <F label="Notes"><textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={`${inp} h-auto py-2`} data-testid="docs-notes-input" /></F>
          <button type="submit" disabled={busy} className="w-full h-11 rounded-sm bg-primary text-primary-foreground font-medium hover:bg-[#33B9FF] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="docs-submit-button">
            <Plus className="w-4 h-4" /> {busy ? "…" : t("docs.generate")}
          </button>
        </form>

        <div className="p-6 border border-border rounded-sm bg-card" data-testid="docs-list">
          <div className="text-overline mb-5">RECENT · {docs.length}</div>
          <div className="space-y-3">
            {docs.length === 0 ? (
              <div className="text-sm text-muted-foreground font-mono">No documents yet.</div>
            ) : docs.map(d => (
              <div key={d.document_id} className="flex items-center gap-3 p-3 border border-border/60 rounded-sm hover:border-primary/40 transition-colors" data-testid={`docs-row-${d.document_id}`}>
                <div className="w-9 h-9 rounded-sm bg-primary/10 border border-primary/25 flex items-center justify-center"><FileText className="w-4 h-4 text-primary" /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{d.name}</div>
                  <div className="text-[10px] font-mono tracking-widest uppercase text-muted-foreground mt-0.5">{d.type} · {Math.round((d.size_bytes || 0) / BYTES_PER_KB)} KB · {new Date(d.created_at).toLocaleString()}</div>
                </div>
                <button onClick={() => download(d)} className="inline-flex items-center gap-2 px-3 h-9 rounded-sm border border-border hover:border-primary hover:text-primary text-[10px] font-mono tracking-widest uppercase text-secondary-foreground transition-colors" data-testid={`docs-download-${d.document_id}`}>
                  <Download className="w-3.5 h-3.5" /> {t("docs.download")}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full h-10 px-3 rounded-sm bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none font-mono text-sm text-white";
function F({ label, children }) { return <label className="block"><div className="mb-1.5 text-[10px] font-mono tracking-[0.22em] text-muted-foreground uppercase">{label}</div>{children}</label>; }
